import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getApiBaseUrl } from '../utils/urlHelper';

const API = getApiBaseUrl();
const RESOLVE = (u) => (!u ? '' : u.startsWith('http') ? u : `${API}${u}`);

/**
 * Iter 330 — HeroMusicProvider
 * -------------------------------------------------------------------
 * Persistent, playlist-aware background audio player for the whole app.
 * Mounted ABOVE <Routes/> so navigation between pages does NOT stop the
 * music. Autoplay starts muted (required by browser policies); the user
 * explicitly unmutes via a visible control in the Hero section.
 *
 * Playlist (2-5 tracks typical) is pulled from the homepage settings
 * document (`hero.music_tracks` + `hero.music_enabled`). When the current
 * track ends the next one in the playlist starts; the list loops.
 */
const HeroMusicContext = createContext(null);

export const useHeroMusic = () => {
  const ctx = useContext(HeroMusicContext);
  if (!ctx) throw new Error('useHeroMusic must be used within HeroMusicProvider');
  return ctx;
};

export const HeroMusicProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  // Fetch config once on mount
  useEffect(() => {
    fetch(`${API}/api/homepage-settings`)
      .then(r => r.json())
      .then(d => {
        const h = d?.hero || {};
        const list = Array.isArray(h.music_tracks) ? h.music_tracks.filter(t => t && t.url) : [];
        setTracks(list);
        setEnabled(!!h.music_enabled && list.length > 0);
      })
      .catch(() => {});
  }, []);

  // Auto-start playback (muted) whenever the config loads
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !enabled || tracks.length === 0) return;
    a.muted = muted;
    const src = RESOLVE(tracks[trackIndex]?.url);
    if (a.src !== src) a.src = src;
    if (!paused) {
      const play = a.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    }
  }, [enabled, tracks, trackIndex, muted, paused]);

  const next = useCallback(() => {
    setTrackIndex(i => (tracks.length ? (i + 1) % tracks.length : 0));
  }, [tracks.length]);

  const prev = useCallback(() => {
    setTrackIndex(i => (tracks.length ? (i - 1 + tracks.length) % tracks.length : 0));
  }, [tracks.length]);

  const toggleMute = useCallback(() => setMuted(m => !m), []);
  const togglePause = useCallback(() => {
    setPaused(p => {
      const a = audioRef.current;
      if (!a) return !p;
      if (p) { a.play().catch(() => {}); } else { a.pause(); }
      return !p;
    });
  }, []);

  const currentTrack = tracks[trackIndex] || null;

  return (
    <HeroMusicContext.Provider
      value={{ enabled, tracks, trackIndex, muted, paused, currentTrack, next, prev, toggleMute, togglePause }}
    >
      {enabled && (
        <audio
          ref={audioRef}
          onEnded={next}
          autoPlay
          loop={tracks.length <= 1}
          playsInline
          data-testid="hero-music-audio"
          style={{ position: 'absolute', width: 0, height: 0 }}
        />
      )}
      {children}
    </HeroMusicContext.Provider>
  );
};

export default HeroMusicProvider;
